import { MouseEvent } from "react";
import { Checkbox, Tooltip } from "@mui/material";
import { spatialDatasetLayerId } from "../../constants/spatialDataset";
import { useLayerSelector } from "../../store/layer";
import { useSpatialDatasetLayerVisibility } from "../../hooks/useSpatialDatasetLayerVisibility";
import type { FlexibleDataItem } from "../../types";

interface SpatialDatasetVisibilityCheckboxProps {
  item: FlexibleDataItem;
}

/**
 * Top-right "show on map" control for a spatial-dataset tile/row.
 * Reads and writes `visibleLayers` so it stays in sync with the legend.
 */
export default function SpatialDatasetVisibilityCheckbox({ item }: SpatialDatasetVisibilityCheckboxProps) {
  const datasetType = (item as any).dataset_type as string | undefined;
  const status = (item as any).processing_status as string | undefined;
  const displayUrl = (item as any).display_url as string | undefined;
  const isTabular = datasetType === "tabular";
  const canShow = !isTabular && status === "completed" && Boolean(displayUrl || item.id);

  const layerId = spatialDatasetLayerId(item.id);
  const { visibleLayers } = useLayerSelector();
  const { setVisible, pendingId } = useSpatialDatasetLayerVisibility();
  const checked = visibleLayers.has(layerId);
  const busy = pendingId === layerId;

  if (isTabular || status !== "completed") return null;

  const stop = (e: MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Tooltip title={checked ? "Hide on map" : "Show on map"}>
      <Checkbox
        size='small'
        checked={checked}
        disabled={!canShow || busy}
        inputProps={{ "aria-label": checked ? "Hide on map" : "Show on map" }}
        onMouseDown={stop}
        onClick={stop}
        onChange={(e) => {
          e.stopPropagation();
          void setVisible(item as any, e.target.checked);
        }}
        sx={{
          color: "rgba(255,255,255,0.85)",
          "&.Mui-checked": { color: "#fff" },
          p: 0.25,
          bgcolor: "rgba(16, 24, 40, 0.55)",
          borderRadius: "4px"
        }}
      />
    </Tooltip>
  );
}
